/**
 * Global backstop for stale-chunk failures.
 *
 * `lazyWithReload` (this package) already wraps every code-split
 * route. This file is the safety net for the *other* dynamic imports
 * scattered through the codebase — `await import('./foo')` inside
 * event handlers, conditional `import()` in main.tsx, third-party
 * libraries that lazy-load their own subchunks, etc.
 *
 * We listen for the two browser hooks that catch unhandled async
 * errors:
 *
 *   - `window.addEventListener('unhandledrejection', ...)` for any
 *     promise that rejects with no .catch handler. This is what
 *     `import('./missing-chunk.js')` produces.
 *   - `window.addEventListener('error', ...)` for synchronous script
 *     errors and for `<script>` 4xx events.
 *
 * Both hooks defer to the same debounce + reload helper as
 * `lazyWithReload`, so we get one consistent "auto-reload at most
 * once per 10s" behaviour across the app.
 *
 * Call once from each app's main.tsx, before mounting React.
 */

import { isChunkLoadError, scheduleChunkReload } from './lazyWithReload';

let installed = false;

export function installChunkReloadHandler(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (!isChunkLoadError(event.reason)) return;
    // Prevent the noisy default "Uncaught (in promise) ..." console
    // log — we're handling it intentionally.
    event.preventDefault();
    scheduleChunkReload();
  });

  window.addEventListener('error', (event: ErrorEvent) => {
    // `event.error` is set for thrown errors; `event.message` covers
    // the bare ScriptError case. We check both.
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) {
      event.preventDefault();
      scheduleChunkReload();
    }
  });
}
